'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { ClerkProvider } from '@clerk/nextjs';
import { SonnerToaster } from '@extern/ui/sonner';
import { TooltipProvider } from '@extern/ui/tooltip';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import { ReactQueryProvider } from '@/infra/react-query/react-query-provider';
import { PageView } from '@/lib/analytics/page-view';
import { PostHogProvider } from '@/lib/analytics/posthog-provider';
import { allowsThirdPartyTracking } from '@/lib/analytics/third-party-tracking-routes';
import { SegmentScript } from './segment-script';
import { TikTokPixelScript } from './tik-tok-pixel-script';

/**
 * Loaded on demand so the session recorder is not merely un-initialised on an untracked
 * route, but never DOWNLOADED there.
 *
 * A static import puts LogRocket's UMD bundle in the shared client chunk, so `/try/*` and
 * `/journey` were fetching the recorder for anonymous visitors and then deciding not to
 * start it. No data was ever sent, but "we ship a session recorder to people who have
 * consented to nothing and trust ourselves not to call init" is a much harder sentence to
 * defend in a privacy review than "we do not send it at all".
 *
 * `ssr: false` because this only ever runs in the browser; it renders nothing.
 */
const LogRocketInit = dynamic(() => import('./log-rocket-init').then(m => ({
  default: m.LogRocketInit
})), {
  ssr: false
});
interface AppProvidersProps {
  children?: ReactNode;
}
export function AppProviders(t0) {
  const $ = _c(19);
  const {
    children
  } = t0;
  const t1 = usePathname();
  let t2;
  if ($[0] !== t1) {
    t2 = allowsThirdPartyTracking(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const thirdPartyTracking = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SonnerToaster />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== thirdPartyTracking) {
    t4 = thirdPartyTracking ? <LogRocketInit /> : null;
    $[3] = thirdPartyTracking;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PageView />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== children || $[7] !== t4) {
    t6 = <ReactQueryProvider><NuqsAdapter><TooltipProvider>{children}{t3}{t4}{t5}</TooltipProvider></NuqsAdapter></ReactQueryProvider>;
    $[6] = children;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6 || $[10] !== thirdPartyTracking) {
    t7 = <ClerkProvider><PostHogProvider surveys={thirdPartyTracking}>{t6}</PostHogProvider></ClerkProvider>;
    $[9] = t6;
    $[10] = thirdPartyTracking;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  let t9;
  if ($[12] !== thirdPartyTracking) {
    t8 = <SegmentScript appcues={thirdPartyTracking} />;
    t9 = thirdPartyTracking ? <TikTokPixelScript /> : null;
    $[12] = thirdPartyTracking;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = <>{t7}{t8}{t9}</>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}